/**
 * @file HaulagePoLinkDialog.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-07
 * @modified 2026-10-08
 * @description "Haulage for a PO" — link a haulier's bill to a
 *              PO made in Xero that it carried the goods for (N Hayes'
 *              INV-0532 for Willett's PO-26135). The PO is found by its
 *              number, whoever's it is (GET /api/bills/haulage-po, or
 *              /api/bills/:id/haulage-po for a saved bill); the server says
 *              whether it may be linked — a PO raised here is pointed at its
 *              PO-H. Linking changes nothing on the PO (cost, status) and
 *              nothing in Xero. Used by the bill window's PO links and by Add
 *              Bill (utils/billHaulageLink).
 *
 * 1.0.1 (2026-10-07): on Add Bill the chosen supplier is sent, so the server
 *   refuses the supplier's own PO or its supplier group's — that is the goods
 *   the bill bought, not another supplier's.
 * 1.1.0 (2026-10-08, Cam): renamed "Haulage for a PO". The supplier's OWN
 *   PO made in Xero may be chosen too — its separate haulage bill (a PO-
 *   sent for the product only, then a product bill and a haulage bill); the
 *   server allows it here (the user's choice), flagged `same_supplier`, and
 *   still never offers it at save time.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort } from '../utils/dateFormatters';
import './BillLocalEdits.css';

function HaulageBody({ number, setNumber, onCheck, checking, found, reason, setReason, linking }) {
  useSlideOutButton([checking, linking, !!found?.allowed]);
  const po = found?.po;
  return (
    <div className="bill-local-body">
      <p className="bill-local-hint">
        For a haulage bill on a PO made in Xero: a haulier&apos;s bill for another supplier&apos;s goods, or the
        supplier&apos;s own separate haulage bill for its product PO. The PO&apos;s cost and status are not changed,
        it does not count as billing the goods, and nothing is sent to Xero. Any PO can be chosen — it is a record.
      </p>
      <div className="filter-group">
        <label htmlFor="haulage-po-number">PO number</label>
        <Input
          id="haulage-po-number"
          type="text"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onEnter={onCheck}
          placeholder="PO-26135"
        />
      </div>
      {found && (
        <div className={found.allowed ? 'bill-local-hint' : 'bill-local-error'} role={found.allowed ? undefined : 'alert'}>
          {po && (
            <div>
              <strong>{po.purchase_order_number}</strong>
              {po.contact_name ? ` · ${po.contact_name}` : ''}
              {po.customer_name ? ` · for ${po.customer_name}` : ''}
              {(po.delivery_date || po.date) ? ` · ${formatDateShort(po.delivery_date || po.date)}` : ''}
              {po.status ? ` · ${po.status}` : ''}
            </div>
          )}
          <div>{found.message}</div>
        </div>
      )}
      {found?.allowed && (
        <div className="filter-group">
          <label htmlFor="haulage-po-reason">Why (optional, kept in the history)</label>
          <Textarea id="haulage-po-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="e.g. N Hayes carried Willett's load to Mounticombe" />
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {number|null} props.billId — the saved bill, or null on Add Bill
 * @param {string} [props.transactionType] — Add Bill's type (only a bill can be haulage)
 * @param {Array<number>} [props.excludeIds] — POs already on the bill
 * @param {function} props.onClose
 * @param {function} props.onLink — (po, reason) once the user confirms
 * @param {boolean} [props.linking]
 */
function HaulagePoLinkDialog({
  isOpen, billId = null, transactionType = 'BILL', supplierAccountId = null, supplierXeroContactId = null,
  excludeIds = [], onClose, onLink, linking = false,
}) {
  const [number, setNumber] = useState('');
  const [found, setFound] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setNumber('');
    setFound(null);
    setReason('');
  }, [isOpen]);

  // A new number is a new question.
  useEffect(() => { setFound(null); }, [number]);

  const check = async () => {
    if (!number.trim()) return;
    setChecking(true);
    try {
      const qs = new URLSearchParams({ number: number.trim() });
      if (!billId) {
        qs.set('transaction_type', transactionType || 'BILL');
        if (supplierAccountId) qs.set('account_id', String(supplierAccountId));
        if (supplierXeroContactId) qs.set('xero_contact_id', String(supplierXeroContactId));
      }
      const url = billId ? `${API_URL}/api/bills/${billId}/haulage-po?${qs}` : `${API_URL}/api/bills/haulage-po?${qs}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'The PO could not be looked up.');
      if (data.po && excludeIds.map(Number).includes(Number(data.po.id))) {
        setFound({ ...data, allowed: false, message: `${data.po.purchase_order_number} is already on this bill.` });
      } else {
        setFound(data);
      }
    } catch (err) {
      setFound({ po: null, allowed: false, message: err.message });
    } finally {
      setChecking(false);
    }
  };

  const allowed = !!found?.allowed && !!found?.po;
  return (
    <Modal
      isOpen={isOpen}
      onClose={linking ? () => {} : onClose}
      title="Haulage for a PO"
      size="md"
      className="bill-local"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={linking}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={check} disabled={checking || linking || !number.trim()}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">search</span></div>
              <span className="btn-label">{checking ? 'Finding…' : 'Find PO'}</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" data-write-action onClick={() => onLink(found.po, reason.trim() || null)}
                    disabled={!allowed || linking} title={allowed ? found.message : 'Find a PO made in Xero first'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">local_shipping</span></div>
              <span className="btn-label">{linking ? 'Linking…' : 'Link as haulage bill'}</span>
            </button>
          </div>
        </div>
      }
    >
      {isOpen && (
        <HaulageBody
          number={number} setNumber={setNumber} onCheck={check} checking={checking}
          found={found} reason={reason} setReason={setReason} linking={linking}
        />
      )}
    </Modal>
  );
}

export default HaulagePoLinkDialog;
